import { findImageForList, findImageID, findUnpairedImageID, getdateTimeDiff } from "./Utils";
import { getCamerabyId } from "../../model/reportService";
import { getPairedCamSnapShotIds, getUnpairedCamSnapShotIds } from "../../model/cameraonboardingService";
import { ImageDownloadMedia } from "../../model/mediaService";
import { saveStreamImageMapping, saveUnpairedCameImageMapping } from "../../store/actions";
import moment from "moment-timezone";

export const StreamType = {
  PAIRED_CAMERA: "PAIRED_CAMERA",
  UNPAIRED_CAMERA: "UNPAIRED_CAMERA"
};

// Constants for cache validation
const CACHE_EXPIRY_MINUTES = 1440; // 24 hours in minutes
const INVALID_DATE_STRING = "Invalid Date";

/**
 * Service to fetch snapshot image URL based on parameters
 * @param {string} streamId - The stream ID
 * @param {string} cameraId - The camera ID
 * @param {string} streamType - The stream type (PAIRED_CAMERA or UNPAIRED_CAMERA)
 * @param {string} companionId - The companion ID (optional)
 * @param {object} reduxStore - Object containing all Redux state and dispatch
 * @param {object} reduxStore.activeSession - The active session object
 * @param {array} reduxStore.reducerPairedImages - Redux state for paired images
 * @param {array} reduxStore.reducerUnpairedImages - Redux state for unpaired images
 * @param {array} reduxStore.reducerStreamList - Redux state for stream list
 * @param {array} reduxStore.reducerUnpairedCameraList - Redux state for unpaired camera list
 * @param {function} reduxStore.dispatch - Redux dispatch function for saving image mappings
 * @returns {Promise<object>} Object containing imageUrl, timeStamp, and loading state
 */
export const fetchSnapshotImage = async (streamId, cameraId, streamType, companionId = "", reduxStore) => {
  const { activeSession, reducerPairedImages, reducerUnpairedImages, reducerStreamList, reducerUnpairedCameraList, dispatch } = reduxStore;
  try {
    const customData =
      streamType === StreamType.PAIRED_CAMERA ? findImageID(reducerStreamList, streamId) : findUnpairedImageID(reducerUnpairedCameraList, streamId);

    const matchingImageItem = findImageForList(streamType === StreamType.PAIRED_CAMERA ? reducerPairedImages : reducerUnpairedImages, streamId);

    // Check if we have cached image that's still valid (less than 24 hours old)
    if (matchingImageItem) {
      if (
        getdateTimeDiff(Date.now(), matchingImageItem.timeStamp) <= CACHE_EXPIRY_MINUTES &&
        new Date(matchingImageItem.timeStamp).toString() !== INVALID_DATE_STRING
      ) {
        return {
          imageUrl: matchingImageItem.imageUrl,
          timeStamp: moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
          loading: false
        };
      }
    }

    // Check if we have custom data with valid timestamp
    if (customData?.imageId) {
      if (
        getdateTimeDiff(Date.now(), customData?.imagetimestamp) <= CACHE_EXPIRY_MINUTES &&
        new Date(customData?.imagetimestamp).toString() !== INVALID_DATE_STRING
      ) {
        const imageUrl = await fetchImageById(customData.imageId, customData.imagetimestamp, activeSession, streamId, "snapshot");
        return {
          imageUrl,
          timeStamp: moment(customData.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
          loading: false
        };
      }
    }

    // Fetch new snapshot from API
    const result = await getSnapshotFromApi(streamType, streamId, cameraId, companionId, activeSession, customData, matchingImageItem, dispatch);

    return result;
  } catch (error) {
    console.error("Error in fetchSnapshotImage:", error);
    throw error;
  }
};

export const fetchFovSnapshotImage = async (streamId, cameraId, streamType, companionId = "", reduxStore, isForceGet = true) => {
  const { activeSession, reducerPairedImages, reducerUnpairedImages, reducerStreamList, reducerUnpairedCameraList, dispatch } = reduxStore;
  try {
    const customData =
      streamType === StreamType.PAIRED_CAMERA ? findImageID(reducerStreamList, streamId) : findUnpairedImageID(reducerUnpairedCameraList, streamId);

    const matchingImageItem = findImageForList(streamType === StreamType.PAIRED_CAMERA ? reducerPairedImages : reducerUnpairedImages, streamId);

    // Check if we have cached image that's still valid (less than 24 hours old)
    if (matchingImageItem) {
      
        return {
          imageUrl: matchingImageItem.imageUrl,
          timeStamp: moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
          loading: false
      }
    }

    // Check if we have custom data with valid timestamp
    if (customData?.imageId) {
      
        const imageUrl = await fetchImageById(customData.imageId, customData.imagetimestamp, activeSession, streamId, "snapshot");
        return {
          imageUrl,
          timeStamp: moment(customData.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
          loading: false
        };
      
    }
    if(isForceGet){
    // Fetch new snapshot from API
      const result = await getSnapshotFromApi(streamType, streamId, cameraId, companionId, activeSession, customData, matchingImageItem, dispatch);
      return result;
    }
  } catch (error) {
    console.error("Error in fetchSnapshotImage:", error);
    throw error;
  }
};

/**
 * Get snapshot from API based on stream type
 */
const getSnapshotFromApi = async (streamType, streamId, cameraId, companionId, activeSession, customData, matchingImageItem, dispatch) => {
  if (streamType === StreamType.PAIRED_CAMERA) {
    return await handlePairedSnapshot(streamId, cameraId, activeSession, customData, matchingImageItem, dispatch);
  } else {
    return await handleUnpairedSnapshot(streamId, cameraId, companionId, activeSession, customData, matchingImageItem, dispatch);
  }
};

/**
 * Handle paired camera snapshot
 */
const handlePairedSnapshot = async (streamId, cameraId, activeSession, customData, matchingImageItem, dispatch) => {
  try {
    const [cameraDetails] = getCamerabyId(cameraId);
    const [fovData] = await getPairedCamSnapShotIds(activeSession, cameraDetails?.qrcode, streamId);

    if (fovData?.imageid) {
      const imageUrl = await fetchImageById(fovData?.imageid, fovData?.imagetimestamp, activeSession, streamId, "snapshot");

      // Save to Redux store
      if (dispatch) {
        dispatch(saveStreamImageMapping(streamId, imageUrl, fovData?.imagetimestamp));
      }

      return {
        imageUrl,
        timeStamp: moment(fovData?.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    } else if (customData?.imageId) {
      const imageUrl = await fetchImageById(customData.imageId, customData.imagetimestamp, activeSession, streamId, "snapshot");
      return {
        imageUrl,
        timeStamp: moment(customData.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    }

    return {
      imageUrl: "",
      timeStamp: "",
      loading: false
    };
  } catch (error) {
    console.error("Error fetching paired snapshot:", error);

    // Fallback to cached image if available
    if (matchingImageItem) {
      return {
        imageUrl: matchingImageItem.imageUrl,
        timeStamp: moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    } else if (customData?.imageId) {
      const imageUrl = await fetchImageById(customData.imageId, customData.imagetimestamp, activeSession, streamId);
      return {
        imageUrl,
        timeStamp: moment(customData.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    }

    throw error;
  }
};

/**
 * Handle unpaired camera snapshot
 */
const handleUnpairedSnapshot = async (streamId, cameraId, companionId, activeSession, customData, matchingImageItem, dispatch) => {
  try {
    const fovData = await getUnpairedCamSnapShotIds(activeSession, companionId, cameraId, [streamId], "snapshot");

    if (fovData && !fovData?.hasOwnProperty("error")) {
      if (fovData?.[0]?.imageid) {
        const imageUrl = await fetchImageById(fovData[0].imageid, fovData[0].timestamp, activeSession, streamId);

        // Save to Redux store
        if (dispatch) {
          dispatch(saveUnpairedCameImageMapping(streamId, imageUrl, fovData[0].timestamp));
        }

        return {
          imageUrl,
          timeStamp: moment(fovData[0].timestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
          loading: false
        };
      }
    }

    return {
      imageUrl: "",
      timeStamp: "",
      loading: false
    };
  } catch (error) {
    console.error("Error fetching unpaired snapshot:", error);

    // Fallback to cached image if available
    if (matchingImageItem) {
      return {
        imageUrl: matchingImageItem.imageUrl,
        timeStamp: moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    } else if (customData?.imageId) {
      const imageUrl = await fetchImageById(customData.imageId, customData.imagetimestamp, activeSession, streamId);
      return {
        imageUrl,
        timeStamp: moment(customData.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    }

    throw error;
  }
};

/**
 * Fetch image by ID
 */
const fetchImageById = async (imageId, timeStamp, activeSession, streamId, mediaSource) => {
  try {
    const url = await ImageDownloadMedia(imageId, activeSession, streamId, mediaSource);
    if (!url) {
      throw new Error("Image download failed");
    }
    return url;
  } catch (error) {
    console.error("Error downloading image:", error);
    throw error;
  }
};

/**
 * Refetch snapshot image - convenience function for refreshing
 */
export const refetchSnapshotImage = async (streamId, cameraId, streamType, companionId = "", reduxStore) => {
  const { activeSession, dispatch } = reduxStore;
  // Force refetch by bypassing cache
  if (streamType === StreamType.PAIRED_CAMERA) {
    const [cameraDetails] = getCamerabyId(cameraId);
    const [fovData] = await getPairedCamSnapShotIds(activeSession, cameraDetails?.qrcode, streamId);

    if (fovData?.imageid) {
      const imageUrl = await fetchImageById(fovData.imageid, fovData?.imagetimestamp, activeSession, streamId);

      if (dispatch) {
        dispatch(saveStreamImageMapping(streamId, imageUrl, fovData?.imagetimestamp));
      }

      return {
        imageUrl,
        timeStamp: moment(fovData?.imagetimestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    }
  } else {
    const [fovData] = await getUnpairedCamSnapShotIds(activeSession, companionId, cameraId, [streamId]);

    if (fovData && !fovData?.hasOwnProperty("error") && fovData?.imageid) {
      const imageUrl = await fetchImageById(fovData.imageid, fovData?.timestamp, activeSession, streamId);

      if (dispatch) {
        dispatch(saveUnpairedCameImageMapping(streamId, imageUrl, fovData?.timestamp));
      }

      return {
        imageUrl,
        timeStamp: moment(fovData?.timestamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"),
        loading: false
      };
    }
  }

  return {
    imageUrl: "",
    timeStamp: "",
    loading: false
  };
};
